import cn from "app/utils/classnames";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type ChangeEvent,
} from "react";

export interface SpellingInputHandle {
  // Insert text at the current caret position
  insertText: (text: string) => void;

  // Replaces current input value
  setText: (newText: string) => void;

  // Clears all text
  clearText: () => void;

  // Backspace from the current caret position
  backspace: () => void;

  forceFocus: () => void;
}

interface SpellingInputProps {
  maxTextLength?: number;

  disabled?: boolean;

  // Key up/down events from the input element
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  // Emitted any time the input element's text changes
  onInputChange?: (newText: string) => void;

  className?: string;

  // If true, the input will be visibly hidden and non-interactive, but it will
  // technically still exist. Use this if you want to temporarilly hide the
  // input while preserving its state.
  hide?: boolean;
}

// Wrapper for the input element used when spelling words in the game.
const SpellingInput = forwardRef<SpellingInputHandle, SpellingInputProps>(
  (props: SpellingInputProps, ref) => {
    props.maxTextLength ??= 45;

    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      // Manually inserts a new character into the input at its current
      // cursor position.
      insertText(text: string) {
        if (inputRef.current == null || !text || props.disabled) return;

        const input = inputRef.current;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const currText = input.value;

        let newText = currText.slice(0, start) + text + currText.slice(end);
        newText = newText.slice(0, props.maxTextLength);
        input.value = newText;

        input.selectionStart = input.selectionEnd = start + text.length;
        input.focus();

        props.onInputChange?.(input.value);
      },

      setText(newText: string) {
        if (inputRef.current == null || !newText || props.disabled) return;

        const input = inputRef.current;

        newText = newText.slice(0, props.maxTextLength);
        input.value = newText;
        input.selectionStart = input.selectionEnd = newText.length;

        props.onInputChange?.(input.value);
      },

      clearText() {
        if (inputRef.current == null || props.disabled) return;

        const input = inputRef.current;

        input.selectionStart = input.selectionEnd = 0;
        input.value = "";

        props.onInputChange?.(input.value);
      },

      backspace() {
        if (inputRef.current == null || props.disabled) return;

        const input = inputRef.current;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        let newText = input.value;

        if (start === end) {
          newText = newText.slice(0, start - 1) + newText.slice(end);
          input.value = newText;
          input.selectionStart = input.selectionEnd = start - 1;
        } else {
          newText =
            newText.slice(0, start) + newText.slice(end, newText.length);
          input.value = newText;
          input.selectionStart = input.selectionEnd = start;
        }

        input.focus();

        props.onInputChange?.(input.value);
      },

      forceFocus() {
        if (props.disabled) return;
        inputRef.current?.focus();
      },
    }));

    // Make sure the input element comes back into focus whenever it's reenabled
    useEffect(() => {
      if (!props.disabled) {
        inputRef.current?.focus();
      }
    }, [props.disabled]);

    function handleInputBlur() {
      inputRef.current?.focus();
    }

    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
      // Remove anything that's not a letter
      let sanitizedInput = e.target.value.replace(/[^a-zA-Z]/g, "");
      sanitizedInput = sanitizedInput.toLowerCase();
      sanitizedInput = sanitizedInput.slice(0, props.maxTextLength);
      e.target.value = sanitizedInput;

      props.onInputChange?.(sanitizedInput);
    }

    return (
      <input
        ref={inputRef}
        className={cn(
          "w-full px-2.5 py-0.75",
          "font-mono text-md lg:text-xl uppercase",
          "bg-background border border-black rounded-sm",
          "select-none",
          "focus:outline-gray-300 focus:outline-2",
          props.className,
        )}
        placeholder="start typing..."
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        inputMode="none"
        autoFocus
        data-1p-ignore
        onKeyDown={props.onKeyDown}
        onKeyUp={props.onKeyUp}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        disabled={props.disabled}
      />
    );
  },
);

export default SpellingInput;
